import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal/react';
import * as React from 'react';
import { apiModelTypeToAutomationStepType } from '../../../../extViewmodels/sdk/Utils';
import { IImpersonationContextComponentProps } from '../../../../models';
import { AutomationEditorStep, AutomationStepAction, getStepType } from '../../../../models/Automations';
import { useEnvironment, useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDefaultRichContentForTextingStep } from '../../../../models/UiUtils';
import {
	useAutomationTemplateStepActionItemCreateMutation,
	useAutomationTemplateStepActionItemUpdateMutation,
	useAutomationTemplateStepAddTagCreateMutation,
	useAutomationTemplateStepAddTagUpdateMutation,
	useAutomationTemplateStepDeleteMutation,
	useAutomationTemplateStepEmailCreateMutation,
	useAutomationTemplateStepEmailUpdateMutation,
	useAutomationTemplateStepHtmlNewsletterUpdateMutation,
	useAutomationTemplateStepHwcCreateMutation,
	useAutomationTemplateStepHwcUpdateMutation,
	useAutomationTemplateStepNewsletterCreateMutation,
	useAutomationTemplateStepNoActionCreateMutation,
	useAutomationTemplateStepNoActionUpdateMutation,
	useAutomationTemplateStepOpportunityCreateMutation,
	useAutomationTemplateStepOpportunityUpdateMutation,
	useAutomationTemplateStepRemoveTagCreateMutation,
	useAutomationTemplateStepRemoveTagUpdateMutation,
	useAutomationTemplateStepTextingCreateMutation,
	useAutomationTemplateStepTextingUpdateMutation,
} from '../../../../queries';
import { useAutomationTemplateSupportedStepsQuery } from '../../../../queries/Automation/queries/useAutomationTemplateSupportedStepsQuery';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { IConnectionTypeComponentProps, withConnectionTypes } from '../../../containers/templates/EditCampaign/context';
import { bs } from '../../../styles/styles';
import { Collapsible } from '../../Collapsible';
import { InputFieldError } from '../../InputFieldError';
import { DefaultSelectBox, ISelectBoxOption } from '../../SelectBox';
import { AutomationStepIcon } from '../../svgs/icons/AutomationStepIcon';
import { ActionItemStepConfig } from './components/ActionItemStepConfig';
import { ContactFilterOption } from './components/ContactFilterOption';
import { EmailStepConfig } from './components/EmailStepConfig';
import { HtmlNewsletterStepConfig } from './components/HtmlNewsletterStepConfig';
import { HwcAutomationStepCardContent } from './components/HwcAutomationStepCardContent';
import { NoActionStepConfig } from './components/NoActionStepConfig';
import { OpportunityStepConfig } from './components/OpportunityStepConfig';
import { StepSendFromOption } from './components/StepSendFromOption';
import { TagStepConfig } from './components/TagStepConfig';
import { TextingStepConfig } from './components/TextingStepConfig';
import {
	AdvancedOptionsButton,
	AutomationStepCardSchedule,
	EditAutomationTemplateStepCardHeader,
} from './presentation';
import { styleSheet } from './styles';

const AutomationStepSelectionBoxOption = ({ children }: { children: React.ReactNode }) => {
	return <div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.textTitles, bs.textSm)}>{children}</div>;
};

const StepTypeOptions = [
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='EmailAutomationStep' />
				<span>Send an email</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.Email,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='HtmlNewsletterAutomationStep' />
				<span>Send an HTML newsletter</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.HtmlNewsletter,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='TextingAutomationStep' />
				<span>Send a Text</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.Texting,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='ActionItemAutomationStep' />
				<span>Create an action item</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.ActionItem,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='OpportunityAutomationStep' />
				<span>Create or update an opportunity</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.Opportunity,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='AddTagAutomationStep' />
				<span>Add a tag</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.AddTag,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='RemoveTagAutomationStep' />
				<span>Delete a tag</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.RemoveTag,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='HandwrittenCardAutomationStep' />
				<span>Send a card</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.HandwrittenCard,
	},
	{
		title: () => (
			<AutomationStepSelectionBoxOption>
				<AutomationStepIcon type='NoActionAutomationStep' />
				<span>Do Nothing</span>
			</AutomationStepSelectionBoxOption>
		),
		value: Api.AutomationStepType.NoAction,
	},
];

interface Props
	extends Omit<IImpersonationContextComponentProps, 'impersonationContext'>,
		IConnectionTypeComponentProps {
	index: number;
	onMenuItemClicked?(action: AutomationStepAction): void;
	onRenderTitle?(): React.ReactNode;
	readonly?: boolean;
	steps: AutomationEditorStep[];
	template: Api.AutomationTemplateViewModelType;
	onStepChanged?: () => void;
	// Override to accept both ViewModel and plain object
	// This component already handles both patterns (see isImpersonationValid check)
	impersonationContext?: ImpersonationContextViewModel | Api.IImpersonationContext;
}

function _AutomationStepCard({
	steps,
	index,
	template,
	readonly,
	onMenuItemClicked,
	onRenderTitle,
	impersonationContext,
	connectionTypes,
	onStepChanged,
}: Props) {
	const { logApiError, logEvent } = useEventLogging('AutomationStepCard');
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const environment = useEnvironment();

	// Load supported step types for this template
	const supportedStepsQuery = useAutomationTemplateSupportedStepsQuery({
		id: template?.id || '',
		enabled: !!template?.id,
		impersonationContext,
	});

	const step = steps[index];
	// Convert API _type (e.g., "EmailAutomationStep") to enum format (e.g., "Email")
	const stepType: Api.AutomationStepType | undefined = step.automationStep?._type
		? apiModelTypeToAutomationStepType(step.automationStep._type as Api.ApiModelType)
		: undefined;
	const [showRequiredFieldError, setShowRequiredFieldError] = React.useState(false);

	const trigger =
		template?.parentTemplate?.draftTriggerReference ||
		template?.parentTemplate?.publishedTriggerReference ||
		template?.draftTriggerReference ||
		template?.publishedTriggerReference;
	const [schedule, setSchedule] = React.useState<Api.IAutomationStepSchedule>(() => {
		const automationStep = step?.automationStep;
		if (automationStep?._type) {
			return automationStep?.schedule;
		}
		const criteria =
			trigger?._type === Api.AutomationTriggerType.Meeting ||
			trigger?.resourceSelector?.toLowerCase()?.includes('custom')
				? Api.AutomationStepScheduleCriteria.RelativeToAnchor
				: Api.AutomationStepScheduleCriteria.AfterAutomationStart;
		return {
			criteria: automationStep?.schedule?.criteria || criteria,
			numberOfDays: automationStep?.schedule?.numberOfDays || 0,
			numberOfHours: automationStep?.schedule?.numberOfHours || undefined,
			numberOfMinutes: automationStep?.schedule?.numberOfMinutes || undefined,
		};
	});
	const [name, setName] = React.useState(step.automationStep?._type ? step.automationStep.name : null);
	const [isUpdating, setIsUpdating] = React.useState(false);
	const [tagToAddOnClick, setTagToAddOnClick] = React.useState(
		stepType === Api.AutomationStepType.Email
			? (step.automationStep.model as Api.IEmailAutomationStep).tagToAddOnClick
			: null
	);
	const [_showEmailLinkClickTags, setShowEmailLinkClickTags] = React.useState(
		stepType === Api.AutomationStepType.Email
			? !!(step.automationStep.model as Api.IEmailAutomationStep).tagToAddOnClick
			: false
	);
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState(step.automationStep?.contactFilter);

	// Sync local state with step data changes
	React.useEffect(() => {
		if (!step.automationStep?._type) {
			return;
		}

		// Update schedule if changed
		if (!equal(step.automationStep.schedule, schedule)) {
			setSchedule(step.automationStep.schedule);
		}

		// Update name if changed
		if (step.automationStep.name !== name) {
			setName(step.automationStep.name);
		}

		// Update contact filter if changed
		if (!equal(step.automationStep.contactFilter, skipStepContactFilter)) {
			setSkipStepContactFilter(step.automationStep.contactFilter);
		}

		// Type-specific updates
		if (step.automationStep._type === 'EmailAutomationStep') {
			const emailStep = step.automationStep.model as Api.IEmailAutomationStep;
			if (emailStep.tagToAddOnClick !== tagToAddOnClick) {
				setTagToAddOnClick(emailStep.tagToAddOnClick);
				setShowEmailLinkClickTags(!!emailStep.tagToAddOnClick);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step.automationStep]);

	const hasSendOptionSelected = () => {
		return (
			!!step.automationStep?.sendFromOptions &&
			step.automationStep.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser
		);
	};
	const [_showEmailEditor, setShowEmailEditor] = React.useState(false);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		switch (stepType) {
			case Api.AutomationStepType.Email: {
				const emailStep = step.automationStep.model as Api.IEmailAutomationStep;
				if (!!emailStep.tagToAddOnClick || !!emailStep.options?.sendToSender) {
					return true;
				}
				break;
			}
			default:
				break;
		}

		return skipStepContactFilter?.criteria || hasSendOptionSelected();
	});

	// Sync advanced options state when step data loads
	React.useEffect(() => {
		if (!step.automationStep?._type) {
			return;
		}

		let shouldBeExpanded = false;

		switch (step.automationStep._type as string) {
			case Api.AutomationStepType.Email: {
				const emailStep = step.automationStep.model as Api.IEmailAutomationStep;
				if (!!emailStep.tagToAddOnClick || !!emailStep.options?.sendToSender) {
					shouldBeExpanded = true;
				}
				break;
			}
			case Api.AutomationStepType.Opportunity:
			case Api.AutomationStepType.HandwrittenCard:
				// These have advanced options by default
				break;
			default:
				// Other step types don't require special advanced options handling
				break;
		}

		if (!shouldBeExpanded) {
			shouldBeExpanded = !!skipStepContactFilter?.criteria || hasSendOptionSelected();
		}

		setAdvancedOptionsToggled(shouldBeExpanded);
	}, [step.automationStep?._type, step.automationStep?.model, skipStepContactFilter?.criteria]);

	const clearInlineErrorMessage = () => {
		setShowRequiredFieldError(false);
	};

	const hasAllRequiredInfo = (automationStep: Api.IAutomationStep | null): boolean => {
		if (!automationStep) {
			return false;
		}

		switch (automationStep._type) {
			case 'EmailAutomationStep': {
				const emailStep = automationStep as Api.IEmailAutomationStep;
				return !!(emailStep.subject && emailStep.content && emailStep.name);
			}
			case 'HtmlNewsletterAutomationStep': {
				const newsletterStep = automationStep as Api.IHtmlNewsletterAutomationStep;
				return !!(newsletterStep.content && newsletterStep.name);
			}
			case 'ActionItemAutomationStep': {
				const actionItemStep = automationStep as Api.IActionItemAutomationStep;
				return !!actionItemStep.content;
			}
			case 'AddTagAutomationStep':
			case 'RemoveTagAutomationStep': {
				const tagStep = automationStep as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep;
				return !!tagStep.tagName;
			}
			case 'OpportunityAutomationStep': {
				const opportunityStep = automationStep as Api.IOpportunityAutomationStep;
				return !!(
					opportunityStep.boardReference &&
					opportunityStep.dealSize !== undefined &&
					opportunityStep.dealSize !== null
				);
			}
			default:
				return true;
		}
	};

	const stepEmailCreateMutation = useAutomationTemplateStepEmailCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('EmailStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepEmailUpdateMutation = useAutomationTemplateStepEmailUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('EmailStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepHtmlNewsletterCreateMutation = useAutomationTemplateStepNewsletterCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('NewsletterStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepHtmlNewsletterUpdateMutation = useAutomationTemplateStepHtmlNewsletterUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('NewsletterStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepTextingCreateMutation = useAutomationTemplateStepTextingCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('TextingStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepTextingUpdateMutation = useAutomationTemplateStepTextingUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('TextingStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepActionItemCreateMutation = useAutomationTemplateStepActionItemCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('ActionItemStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepActionItemUpdateMutation = useAutomationTemplateStepActionItemUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('ActionItemStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepAddTagCreateMutation = useAutomationTemplateStepAddTagCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('AddTagStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepAddTagUpdateMutation = useAutomationTemplateStepAddTagUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('AddTagStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepRemoveTagCreateMutation = useAutomationTemplateStepRemoveTagCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('RemoveTagStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepRemoveTagUpdateMutation = useAutomationTemplateStepRemoveTagUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('RemoveTagStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepNoActionCreateMutation = useAutomationTemplateStepNoActionCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('NoActionStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepNoActionUpdateMutation = useAutomationTemplateStepNoActionUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('NoActionStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepOpportunityCreateMutation = useAutomationTemplateStepOpportunityCreateMutation({
		onSuccess: data => {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
			onStepChanged?.();
		},
		onError: error => {
			logApiError('OpportunityStep-Create-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepOpportunityUpdateMutation = useAutomationTemplateStepOpportunityUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('OpportunityStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepHwcCreateMutation = useAutomationTemplateStepHwcCreateMutation({
		onError: error => {
			logApiError(`HandwrittenCardStep-Error`, error);
			errorMessages.pushApiError(error);
		},
	});
	const stepHwcUpdateMutation = useAutomationTemplateStepHwcUpdateMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('HwcStep-Update-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const stepDeleteMutation = useAutomationTemplateStepDeleteMutation({
		onError: (error, variables) => {
			logApiError(`${variables.stepType}-Error`, error);
			errorMessages.pushApiError(error);
		},
	});
	const onHwcTypeSelected = async () => {
		clearInlineErrorMessage();
		const automationStep: Api.HandwrittenCardAutomationStep = {
			_type: 'HandwrittenCardAutomationStep',
			schedule: {
				criteria: Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(trigger)
					? Api.AutomationStepScheduleCriteria.RelativeToAnchor
					: Api.AutomationStepScheduleCriteria.AfterAutomationStart,
				numberOfDays: 0,
				numberOfHours: 0,
				numberOfMinutes: 0,
			},
		};
		if (trigger?.resourceSelector === Api.ResourceSelectorId.HappyBirthday) {
			automationStep.schedule.numberOfDays = -7;
		}
		if (step.automationStep?.id) {
			const stepModelToReplace = step.automationStep;
			const beforeIndex = index + 1 < steps.length ? index + 1 : -1;
			const beforeStepId = beforeIndex > -1 ? steps[beforeIndex]?.automationStep?.id : null;
			// Delete the old step using its original type, not the new type
			await stepDeleteMutation.mutateAsync({
				templateId: template.id,
				stepType: getStepType(stepModelToReplace),
				stepId: stepModelToReplace.id,
				impersonationContext,
			});
			const stepResult = await stepHwcCreateMutation.mutateAsync({
				templateId: template.id,
				step: automationStep,
				beforeId: beforeStepId,
				withEmailFallback: false,
				impersonationContext,
			});
			step.automationStep?.setModel?.(stepResult as Partial<Api.IAutomationStep>);
		} else {
			const beforeIndex = index + 1 < steps.length ? index + 1 : -1;
			const beforeId = beforeIndex > -1 ? steps[beforeIndex]?.automationStep?.id : null;
			const stepResult = await stepHwcCreateMutation.mutateAsync({
				templateId: template.id,
				step: automationStep,
				beforeId,
				withEmailFallback: false,
				impersonationContext,
			});
			step.automationStep?.setModel?.(stepResult as Partial<Api.IAutomationStep>);
		}
	};
	const onTypeSelectionChanged = (selectedOption: ISelectBoxOption<Api.AutomationStepType>) => {
		if (selectedOption.value === stepType) {
			return;
		}
		onStepChanged?.();
		if (selectedOption.value === Api.AutomationStepType.HandwrittenCard) {
			setSkipStepContactFilter(null);
			onHwcTypeSelected();
			return;
		}
		let automationStep: Api.IAutomationStep = null;
		let eventName: string = null;
		switch (selectedOption.value) {
			case Api.AutomationStepType.ActionItem: {
				const actionItemStep: Api.IActionItemAutomationStep = {
					_type: 'ActionItemAutomationStep',
					contactFilter: skipStepContactFilter,
				};
				automationStep = actionItemStep;
				eventName = 'AddActionItemStep';
				break;
			}
			case Api.AutomationStepType.AddTag: {
				const addTagStep: Api.IAddTagAutomationStep = {
					_type: 'AddTagAutomationStep',
					tagName: null,
					contactFilter: skipStepContactFilter,
				};
				automationStep = addTagStep;
				eventName = 'AddAddTagStep';
				break;
			}
			case Api.AutomationStepType.Email: {
				const emailStep: Api.IEmailAutomationStep = {
					_type: 'EmailAutomationStep',
					options: {
						noteVisibility: 'all',
						saveAsNote: true,
					},
					schedule: {
						numberOfHours: schedule?.numberOfHours > 0 ? schedule?.numberOfHours : 11,
					},
					tagToAddOnClick: '',
					contactFilter: skipStepContactFilter,
				};
				automationStep = emailStep;
				eventName = 'AddEmailStep';
				break;
			}
			case Api.AutomationStepType.HtmlNewsletter: {
				const emailStep: Api.IHtmlNewsletterAutomationStep = {
					_type: 'HtmlNewsletterAutomationStep',
					options: {
						noteVisibility: 'all',
						saveAsNote: true,
					},
					schedule: {
						numberOfHours: schedule?.numberOfHours > 0 ? schedule?.numberOfHours : 11,
					},
					tagToAddOnClick: '',
					contactFilter: skipStepContactFilter,
				};
				automationStep = emailStep;
				eventName = 'AddHtmlNewsletterStep';
				break;
			}
			case Api.AutomationStepType.RemoveTag: {
				const removeTagStep: Api.IRemoveTagAutomationStep = {
					_type: 'RemoveTagAutomationStep',
					tagName: null,
					contactFilter: skipStepContactFilter,
				};
				automationStep = removeTagStep;
				eventName = 'AddRemoveTagStep';
				break;
			}
			case Api.AutomationStepType.Texting: {
				const textingStep: Api.ITextingAutomationStep = {
					_type: 'TextingAutomationStep',
					content: getDefaultRichContentForTextingStep(
						template?.impersonationContext?.account || userSession.account.toJs(),
						trigger
					),
					contactFilter: skipStepContactFilter,
					name: 'Texting Step',
				};
				automationStep = textingStep;
				eventName = 'TextingStep';
				break;
			}
			case Api.AutomationStepType.NoAction: {
				const noActionStep: Api.INoActionAutomationStep = {
					_type: 'NoActionAutomationStep',
				};
				automationStep = noActionStep;
				eventName = 'NoActionStep';
				setSkipStepContactFilter(null);
				break;
			}
			case Api.AutomationStepType.Opportunity: {
				const opportunityStep: Api.IOpportunityAutomationStep = {
					_type: 'OpportunityAutomationStep',
					contactFilter: skipStepContactFilter,
				};
				automationStep = opportunityStep;
				eventName = 'OpportunityStep';
				break;
			}
			default: {
				setSkipStepContactFilter(null);
				break;
			}
		}

		if (automationStep) {
			const nextStepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(automationStep);
			automationStep.schedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(
				nextStepType,
				trigger
			);

			if (
				trigger?._type === Api.AutomationTriggerType.Meeting &&
				(automationStep?._type as string) === 'TextingAutomationStep'
			) {
				const textingStep: Api.ITextingAutomationStep = {
					_type: 'TextingAutomationStep',
					content: getDefaultRichContentForTextingStep(
						template?.impersonationContext?.account || userSession.account.toJs(),
						trigger,
						userSession,
						automationStep.schedule
					),
					name: 'Texting Step',
				};
				automationStep = { ...automationStep, ...textingStep };
			}

			const beforeIndex = index + 1 < steps.length ? index + 1 : -1;
			const beforeId = beforeIndex > -1 ? steps[beforeIndex]?.automationStep?.id : null;

			logEvent(eventName, { templateId: template.id });
			setName(automationStep.name ?? null);
			setSchedule(automationStep.schedule);

			const handleStepMutation = async () => {
				try {
					let result: Api.IAutomationStep;

					// Check if we're changing step types on an existing step
					const isTypeChanging = step.automationStep?.id && step.automationStep._type !== automationStep._type;

					if (isTypeChanging) {
						// Step type is changing - must DELETE old step first, then CREATE new one
						// (Can't update a step to a different type)
						const oldStepType = getStepType(step.automationStep);
						await stepDeleteMutation.mutateAsync({
							templateId: template.id,
							stepType: oldStepType,
							stepId: step.automationStep.id,
							impersonationContext,
						});
						// Fall through to create logic below
					} else if (step.automationStep?.id) {
						// Same type, use update mutations
						switch (automationStep._type) {
							case 'EmailAutomationStep':
								result = await stepEmailUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IEmailAutomationStep,
									impersonationContext,
								});
								break;
							case 'HtmlNewsletterAutomationStep':
								result = await stepHtmlNewsletterUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IHtmlNewsletterAutomationStep,
									impersonationContext,
								});
								break;
							case 'TextingAutomationStep':
								result = await stepTextingUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.ITextingAutomationStep,
									impersonationContext,
								});
								break;
							case 'ActionItemAutomationStep':
								result = await stepActionItemUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IActionItemAutomationStep,
									impersonationContext,
								});
								break;
							case 'AddTagAutomationStep':
								result = await stepAddTagUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IAddTagAutomationStep,
									impersonationContext,
								});
								break;
							case 'RemoveTagAutomationStep':
								result = await stepRemoveTagUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IRemoveTagAutomationStep,
									impersonationContext,
								});
								break;
							case 'NoActionAutomationStep':
								result = await stepNoActionUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.INoActionAutomationStep,
									impersonationContext,
								});
								break;
							case 'OpportunityAutomationStep':
								result = await stepOpportunityUpdateMutation.mutateAsync({
									templateId: template.id,
									stepId: step.automationStep.id,
									step: automationStep as Api.IOpportunityAutomationStep,
									impersonationContext,
								});
								break;
							default:
								logApiError('SaveAutomationStep-UnsupportedStepType', {
									message: `Unsupported step type for update: ${automationStep._type}`,
									stepType: automationStep._type,
								} as unknown as Api.IOperationResultNoValue);
								errorMessages.push({
									messages: [`This step type cannot be updated at this time. Please try again later.`],
								});
								return;
						}
						// Update completed, skip create logic
						step.automationStep?.setModel?.(result as Partial<Api.IAutomationStep>);
						return;
					}

					// Create new step (either no ID, or after deleting during type change)
					if (!step.automationStep?.id || isTypeChanging) {
						switch (automationStep._type) {
							case 'EmailAutomationStep':
								result = await stepEmailCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IEmailAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'HtmlNewsletterAutomationStep':
								result = await stepHtmlNewsletterCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IHtmlNewsletterAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'TextingAutomationStep':
								result = await stepTextingCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.ITextingAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'ActionItemAutomationStep':
								result = await stepActionItemCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IActionItemAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'AddTagAutomationStep':
								result = await stepAddTagCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IAddTagAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'RemoveTagAutomationStep':
								result = await stepRemoveTagCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IRemoveTagAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'NoActionAutomationStep':
								result = await stepNoActionCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.INoActionAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							case 'OpportunityAutomationStep':
								result = await stepOpportunityCreateMutation.mutateAsync({
									templateId: template.id,
									step: automationStep as Api.IOpportunityAutomationStep,
									beforeId,
									impersonationContext,
								});
								break;
							default:
								logApiError('SaveAutomationStep-UnsupportedStepType', {
									message: `Unsupported step type for create: ${automationStep._type}`,
									stepType: automationStep._type,
								} as unknown as Api.IOperationResultNoValue);
								errorMessages.push({
									messages: [`This step type cannot be created at this time. Please try again later.`],
								});
								return;
						}
					}

					step.automationStep?.setModel?.(result as Partial<Api.IAutomationStep>);
				} catch (error) {
					logApiError(`${eventName}-Error`, error as Api.IOperationResultNoValue);
					errorMessages.pushApiError(error as Api.IOperationResultNoValue);
				}
			};

			handleStepMutation();
		}
		clearInlineErrorMessage();
	};
	const saveStep = async (
		automationStep?: Api.IAutomationStep,
		newSchedule?: Api.IAutomationStepSchedule,
		loggingEventParams: Api.IDictionary<string | number | boolean> = {}
	) => {
		if (!automationStep || !step.automationStep?.id) {
			return;
		}

		if (newSchedule) {
			automationStep.schedule = newSchedule;
		}

		logEvent('SaveAutomationStep', {
			id: step.automationStep.id,
			type: step.automationStep._type,
			...loggingEventParams,
		});

		try {
			let result: Api.IAutomationStep;

			switch (automationStep._type) {
				case 'EmailAutomationStep':
					result = await stepEmailUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IEmailAutomationStep,
						impersonationContext,
					});
					break;
				case 'HtmlNewsletterAutomationStep':
					result = await stepHtmlNewsletterUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IHtmlNewsletterAutomationStep,
						impersonationContext,
					});
					break;
				case 'TextingAutomationStep':
					result = await stepTextingUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.ITextingAutomationStep,
						impersonationContext,
					});
					break;
				case 'ActionItemAutomationStep':
					result = await stepActionItemUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IActionItemAutomationStep,
						impersonationContext,
					});
					break;
				case 'AddTagAutomationStep':
					result = await stepAddTagUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IAddTagAutomationStep,
						impersonationContext,
					});
					break;
				case 'RemoveTagAutomationStep':
					result = await stepRemoveTagUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IRemoveTagAutomationStep,
						impersonationContext,
					});
					break;
				case 'NoActionAutomationStep':
					result = await stepNoActionUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.INoActionAutomationStep,
						impersonationContext,
					});
					break;
				case 'OpportunityAutomationStep':
					result = await stepOpportunityUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.IOpportunityAutomationStep,
						impersonationContext,
					});
					break;
				case 'HandwrittenCardAutomationStep':
					result = await stepHwcUpdateMutation.mutateAsync({
						templateId: template.id,
						stepId: step.automationStep.id,
						step: automationStep as Api.HandwrittenCardAutomationStep,
						impersonationContext,
					});
					break;
				default:
					logApiError('SaveAutomationStep-UnsupportedStepType', {
						message: `Unsupported step type for save: ${automationStep._type}`,
						stepType: automationStep._type,
					} as unknown as Api.IOperationResultNoValue);
					errorMessages.push({
						messages: [`This step type cannot be saved at this time. Please try again later.`],
					});
					throw new Error(`Unsupported step type: ${automationStep._type}`);
			}

			step.automationStep?.setModel?.(result as Partial<Api.IAutomationStep>);
			onStepChanged?.();
			return result;
		} catch (error) {
			logApiError('SaveAutomationStep-Error', error as Api.IOperationResultNoValue);
			throw error;
		}
	};
	const save = ({
		newSchedule = schedule,
		newTagToAddOnClick = tagToAddOnClick,
		newContactFilter = skipStepContactFilter,
		newSendFrom = undefined,
		newSendToSender = null,
	}: {
		newSchedule?: Api.IAutomationStepSchedule;
		newSendFrom?: Api.ISendFromOptions;
		newTagToAddOnClick?: string;
		newSendToSender?: boolean;
		newContactFilter?: Api.IContactFilterCriteria;
	} = {}) => {
		let automationStep: Api.IAutomationStep = null;
		const eventParams: Api.IDictionary<string | number | boolean> = {};
		switch (stepType) {
			case Api.AutomationStepType.Email: {
				const stepModel: Partial<Api.IEmailAutomationStep> = {
					...(step?.automationStep?.model as Api.IEmailAutomationStep),
					name,
					tagToAddOnClick: newTagToAddOnClick,
					contactFilter: newContactFilter,
				};
				if (newSendToSender != null) {
					stepModel.options = stepModel.options || { noteVisibility: 'all', saveAsNote: true };
					stepModel.options.sendToSender = newSendToSender;
				}
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.HtmlNewsletter: {
				const stepModel: Partial<Api.IHtmlNewsletterAutomationStep> = {
					...(step?.automationStep?.model as Api.IHtmlNewsletterAutomationStep),
					name,
					tagToAddOnClick: newTagToAddOnClick,
					contactFilter: newContactFilter,
				};
				if (newSendToSender != null) {
					stepModel.options = stepModel.options || { noteVisibility: 'all', saveAsNote: true };
					stepModel.options.sendToSender = newSendToSender;
				}
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.Texting: {
				const stepModel: Partial<Api.ITextingAutomationStep> = {
					...(step?.automationStep?.model as Api.ITextingAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.NoAction: {
				const stepModel: Partial<Api.INoActionAutomationStep> = {
					...(step?.automationStep?.model as Api.INoActionAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				break;
			}
			case Api.AutomationStepType.HandwrittenCard: {
				const stepModel: Partial<Api.HandwrittenCardAutomationStep> = {
					...(step?.automationStep?.model as Api.HandwrittenCardAutomationStep),
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				break;
			}
			case Api.AutomationStepType.Opportunity: {
				const stepModel: Partial<Api.IOpportunityAutomationStep> = {
					...(step?.automationStep?.model as Api.IOpportunityAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.ActionItem: {
				const stepModel: Partial<Api.IActionItemAutomationStep> = {
					...(step?.automationStep?.model as Api.IActionItemAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.AddTag: {
				const stepModel: Partial<Api.IAddTagAutomationStep> = {
					...(step?.automationStep?.model as Api.IAddTagAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			case Api.AutomationStepType.RemoveTag: {
				const stepModel: Partial<Api.IRemoveTagAutomationStep> = {
					...(step?.automationStep?.model as Api.IRemoveTagAutomationStep),
					name,
					contactFilter: newContactFilter,
				};
				automationStep = stepModel;
				eventParams.nameLength = name?.length;
				break;
			}
			default: {
				break;
			}
		}

		if (newSendFrom !== undefined) {
			automationStep.sendFromOptions = newSendFrom;
		}

		return saveStep(automationStep, newSchedule);
	};
	const onRequestSetSchedule = (newSchedule: Api.IAutomationStepSchedule) => {
		clearInlineErrorMessage();
		setIsUpdating(true);
		setSchedule(newSchedule);
		save({ newSchedule }).finally(() => {
			setIsUpdating(false);
		});
	};
	const _onNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
		clearInlineErrorMessage();
	};
	const _onEmailCtaClicked = () => {
		setShowEmailEditor(true);
		clearInlineErrorMessage();
	};
	const onTagForLinkClickChanged = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
	};
	const _onAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setShowEmailLinkClickTags(checked);
		if (!checked) {
			onTagForLinkClickChanged('');
		}
	};
	const _onEmailComposerModalRequestClose = (data: Api.IEmailAutomationStep) => {
		if (data) {
			step.automationStep?.setModel?.(data as Partial<Api.IAutomationStep>);
		}
		setShowEmailEditor(false);
	};
	React.useEffect(() => {
		if (!equal(schedule, step?.automationStep?.schedule)) {
			setSchedule(step.automationStep?.schedule);
		}
	}, [step?.automationStep?.schedule, schedule]);
	const showActionMenu =
		stepType !== Api.AutomationStepType.Texting ||
		index > 0 ||
		(steps.length > 1 &&
			index < steps.length - 1 &&
			getStepType(steps[index + 1]?.automationStep) !== Api.AutomationStepType.Switch) ||
		steps.length > 1;
	const canEdit =
		!readonly && (step.automationStep ? step.automationStep.canEdit : true) && (template ? template.canEdit : true);
	// Use query data, fallback to template prop, or show all options as last resort
	const supportedStepModelTypes =
		supportedStepsQuery.data || template?.rootTemplate?.supportedStepModelTypes || template?.supportedStepModelTypes;
	let stepOptions: typeof StepTypeOptions = [...StepTypeOptions]; // Default to all options

	// Only filter if we have actual data (not loading, not empty)
	if (
		!supportedStepsQuery.isLoading &&
		supportedStepModelTypes &&
		Array.isArray(supportedStepModelTypes) &&
		supportedStepModelTypes.length > 0
	) {
		const supportedTypes = new Set(
			supportedStepModelTypes.map((x: string) =>
				Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep({
					_type: x as Api.ApiModelType,
				})
			)
		);
		const filteredOptions = StepTypeOptions.filter(x => supportedTypes.has(x.value));
		// Only use filtered options if we got valid results
		if (filteredOptions.length > 0) {
			stepOptions = filteredOptions;
		}
	}
	// Check if impersonation is valid - support both ViewModel pattern (.isValid) and plain object pattern (account.id)
	const isImpersonationValid =
		(impersonationContext && 'isValid' in impersonationContext
			? impersonationContext.isValid
			: !!impersonationContext?.account?.id) || false;
	if (environment.appType === 'admin' && !isImpersonationValid && stepType !== Api.AutomationStepType.Texting) {
		stepOptions = stepOptions.filter(x => x.value !== Api.AutomationStepType.Texting);
	}
	const isStepBusy =
		stepEmailUpdateMutation.isPending ||
		stepHtmlNewsletterUpdateMutation.isPending ||
		stepTextingUpdateMutation.isPending ||
		stepActionItemUpdateMutation.isPending ||
		stepAddTagUpdateMutation.isPending ||
		stepRemoveTagUpdateMutation.isPending ||
		stepNoActionUpdateMutation.isPending ||
		stepOpportunityUpdateMutation.isPending ||
		stepHwcUpdateMutation.isPending ||
		stepEmailCreateMutation.isPending ||
		stepHtmlNewsletterCreateMutation.isPending ||
		stepTextingCreateMutation.isPending ||
		stepActionItemCreateMutation.isPending ||
		stepAddTagCreateMutation.isPending ||
		stepRemoveTagCreateMutation.isPending ||
		stepNoActionCreateMutation.isPending ||
		stepOpportunityCreateMutation.isPending ||
		stepHwcCreateMutation.isPending ||
		stepDeleteMutation.isPending ||
		isUpdating;

	let errorMessage = null;
	if (showRequiredFieldError && !hasAllRequiredInfo(step.automationStep)) {
		switch (stepType) {
			case Api.AutomationStepType.Email: {
				errorMessage = 'Please provide email content and description/name.';
				break;
			}
			case Api.AutomationStepType.HtmlNewsletter: {
				errorMessage = 'Please select a template and description/name.';
				break;
			}
			case Api.AutomationStepType.ActionItem: {
				errorMessage = 'Please provide action item content.';
				break;
			}
			case Api.AutomationStepType.RemoveTag:
			case Api.AutomationStepType.AddTag: {
				errorMessage = 'Please provide a tag name.';
				break;
			}
			case Api.AutomationStepType.Opportunity: {
				errorMessage = 'Please select a valid board and amount.';
				break;
			}
			default: {
				break;
			}
		}
	}

	const saveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		setIsUpdating(true);
		save({ newSendFrom: option, newSendToSender: sendToSender }).finally(() => {
			setIsUpdating(false);
		});
	};

	const renderFooter = () => {
		const _baseKey = `${step.automationStep?._type}-${step.uuid}-${index}-${step.automationStep?.id || ''}`;

		switch (stepType) {
			case Api.AutomationStepType.Email: {
				return (
					<EmailStepConfig
						key={`email-${step.uuid}-${step.automationStep?.id || 'new'}`}
						step={step}
						canEdit={canEdit}
						templateId={template.id}
						errorMessage={errorMessage}
						trigger={trigger}
						schedule={schedule}
						onClearErrorMessages={clearInlineErrorMessage}
						onStepChanged={onStepChanged}
						impersonationContext={impersonationContext as ImpersonationContextViewModel}
						connectionTypes={connectionTypes}
					/>
				);
			}
			case Api.AutomationStepType.HtmlNewsletter: {
				return (
					<HtmlNewsletterStepConfig
						key={`newsletter-${step.uuid}-${step.automationStep?.id || 'new'}`}
						step={step}
						canEdit={canEdit}
						templateId={template.id}
						errorMessage={errorMessage}
						onClearErrorMessages={clearInlineErrorMessage}
						onStepChanged={onStepChanged}
						impersonationContext={impersonationContext as ImpersonationContextViewModel}
						connectionTypes={connectionTypes}
					/>
				);
			}
			case Api.AutomationStepType.Texting: {
				return (
					<TextingStepConfig
						key={`texting-${step.uuid}-${step.automationStep?.id || 'new'}`}
						step={step}
						canEdit={canEdit}
						templateId={template.id}
						errorMessage={errorMessage}
						trigger={trigger}
						schedule={schedule}
						skipStepContactFilter={skipStepContactFilter}
						saveSenderOptions={saveSenderOptions}
						onClearErrorMessages={clearInlineErrorMessage}
						onStepChanged={onStepChanged}
						onSaveStep={saveStep}
						onFilterChange={filter => {
							setSkipStepContactFilter(filter);
							save({ newContactFilter: filter });
						}}
						connectionTypes={connectionTypes}
						impersonationContext={impersonationContext as ImpersonationContextViewModel}
						index={index}
					/>
				);
			}
			case Api.AutomationStepType.ActionItem: {
				return (
					<ActionItemStepConfig
						key={`actionitem-${step.uuid}-${step.automationStep?.id || 'new'}`}
						step={step}
						canEdit={canEdit}
						templateId={template.id}
						errorMessage={errorMessage}
						onClearErrorMessages={clearInlineErrorMessage}
						onStepChanged={onStepChanged}
						impersonationContext={impersonationContext as ImpersonationContextViewModel}
						connectionTypes={connectionTypes}
					/>
				);
			}
			case Api.AutomationStepType.RemoveTag:
			case Api.AutomationStepType.AddTag: {
				return (
					<TagStepConfig
						key={`tag-${step.uuid}-${step.automationStep?.id || 'new'}`}
						step={step}
						stepType={step.automationStep._type as Api.AutomationStepType.AddTag | Api.AutomationStepType.RemoveTag}
						canEdit={canEdit}
						errorMessage={errorMessage}
						impersonationContext={impersonationContext}
						onClearErrorMessages={clearInlineErrorMessage}
						onSave={async (newSelectedTag: string) => {
							const updatedStep = {
								...step.automationStep,
								tagName: newSelectedTag,
								contactFilter: skipStepContactFilter,
							};
							await saveStep(updatedStep as Api.IAutomationStep);
						}}
					/>
				);
			}
			case Api.AutomationStepType.Opportunity: {
				return (
					<>
						<InputFieldError errorMessage={errorMessage}>
							<OpportunityStepConfig
								key={`opportunity-${step.uuid}-${step.automationStep?.id || 'new'}`}
								canEdit={canEdit && !isStepBusy}
								templateId={template.id}
								step={step.automationStep as Api.IOpportunityAutomationStep}
								clearInlineErrorMessage={clearInlineErrorMessage}
								onStepChanged={onStepChanged}
								impersonationContext={impersonationContext}
							/>
						</InputFieldError>
						<AdvancedOptionsButton
							isCollapsed={!advancedOptionsToggled}
							onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
						/>
						<Collapsible isCollapsed={!advancedOptionsToggled}>
							<div className={css(styleSheet.advancedOptionsContent)}>
								<>
									<StepSendFromOption
										step={step}
										onSave={saveSenderOptions}
										connectionTypes={connectionTypes}
										impersonationContext={impersonationContext as ImpersonationContextViewModel}
										sendFromLabel={'Always assign this opportunity to'}
									/>
									<ContactFilterOption
										canEdit={canEdit}
										skipStepContactFilter={skipStepContactFilter}
										stepUuid={step.uuid}
										stepIsBusy={isStepBusy}
										onFilterChange={filter => {
											setSkipStepContactFilter(filter);
											save({ newContactFilter: filter });
										}}
									/>
								</>
							</div>
						</Collapsible>
					</>
				);
			}
			case Api.AutomationStepType.HandwrittenCard: {
				return (
					<>
						<HwcAutomationStepCardContent
							templateId={template.id}
							canEdit={canEdit}
							step={step}
							trigger={trigger}
							onStepChanged={onStepChanged}
						/>
						<AdvancedOptionsButton
							isCollapsed={!advancedOptionsToggled}
							onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
						/>
						<Collapsible isCollapsed={!advancedOptionsToggled}>
							<div className={css(styleSheet.advancedOptionsContent)}>
								<StepSendFromOption
									step={step}
									onSave={saveSenderOptions}
									connectionTypes={connectionTypes}
									impersonationContext={impersonationContext as ImpersonationContextViewModel}
									sendFromLabel={'Always send this step from'}
								/>
								<ContactFilterOption
									canEdit={canEdit}
									skipStepContactFilter={skipStepContactFilter}
									stepUuid={step.uuid}
									stepIsBusy={isStepBusy}
									onFilterChange={filter => {
										setSkipStepContactFilter(filter);
										save({ newContactFilter: filter });
									}}
								/>
							</div>
						</Collapsible>
					</>
				);
			}
			case Api.AutomationStepType.NoAction: {
				return <NoActionStepConfig />;
			}
			default: {
				break;
			}
		}
	};
	return (
		<div className='automation-step-card'>
			<EditAutomationTemplateStepCardHeader
				canEdit={canEdit}
				onRenderTitle={onRenderTitle}
				ActionMenu={
					showActionMenu ? (
						<Dropdown>
							<DropdownEllipsisTrigger />
							<DropdownContent>
								{stepType !== Api.AutomationStepType.Texting ? (
									<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.Copy)}>
										Duplicate Step
									</DropdownItem>
								) : null}
								{index > 0 ? (
									<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.MoveUp)}>
										Move up a Step
									</DropdownItem>
								) : null}
								{steps.length > 1 &&
								index < steps.length - 1 &&
								getStepType(steps[index + 1]?.automationStep) !== Api.AutomationStepType.Switch ? (
									<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.MoveDown)}>
										Move down a Step
									</DropdownItem>
								) : null}
								{steps.length > 1 ? (
									<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.Delete)}>
										Delete Step
									</DropdownItem>
								) : null}
							</DropdownContent>
						</Dropdown>
					) : null
				}
			/>
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.cardHeader)}>
					<div className={css(styleSheet.cardHeaderInfo)}>
						Select a choice below for this step of the automation flow.
					</div>
					<DefaultSelectBox
						className={css(styleSheet.typeSelect)}
						disabled={!canEdit}
						onSelectionChanged={onTypeSelectionChanged}
						options={stepOptions}
						optionStyles={[styleSheet.typeSelectMenuItem]}
						placeholder={<span className={css(bs.textTitles, bs.textSm)}>Select a choice below...</span>}
						contentClassName={css(styleSheet.typeSelectContent)}
						selectedOption={
							step.automationStep?._type
								? stepOptions.find(
										x => x.value === apiModelTypeToAutomationStepType(step.automationStep?._type as Api.ApiModelType)
									)
								: undefined
						}
					/>
				</div>
				{stepType && (
					<>
						{stepType !== Api.AutomationStepType.HandwrittenCard && (
							<div className={css(styleSheet.cardBody)}>
								<AutomationStepCardSchedule
									canEdit={canEdit}
									index={index}
									onRequestSetSchedule={onRequestSetSchedule}
									schedule={schedule}
									step={step}
									stepType={stepType}
									trigger={trigger}
									isBusy={isUpdating}
								/>
							</div>
						)}

						<div className={css(styleSheet.cardFooter)}>{renderFooter()}</div>
					</>
				)}
			</div>
		</div>
	);
}

export const AutomationStepCard = withConnectionTypes(_AutomationStepCard, {
	refetchOnWindowFocus: false,
});
